import { z } from 'zod';

import { amountSchema } from './money.ts';
import { allergySummarySchema } from './patients.ts';
import { CULTURE_OUTCOMES, LAB_INTERPRETATIONS } from '@wodi/domain';

// The patient portal (docs/plans/phase3-portal-feedback-kiswahili.md §3; ADR 0035). Every
// response here is what a patient may see about themselves or a linked child, after the
// exposure rules (plan §4.2): nothing from a visit with a sensitivity label, validated and
// non-critical lab reports only, no clinical notes. Tokens travel in bodies, never in URLs.

export const UI_LANGUAGES = ['en', 'sw'] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];
export const uiLanguageSchema = z.enum(UI_LANGUAGES);

/** The portal's public code: lower case letters, digits and dashes (plan §2.1). */
export const PORTAL_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{2,31}$/;
export const portalCodeSchema = z.string().trim().toLowerCase().regex(PORTAL_CODE_PATTERN);

/** A one-time code: six digits. */
export const portalOneTimeCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code');

/** Opaque, from POST /portal/sign-in/code. Never in a URL. */
export const portalChallengeTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);

/** GET /portal/:code/info: what the sign-in page may show before anyone signs in. */
export const portalInfoSchema = z.object({ organisationName: z.string(), enabled: z.boolean() });
export type PortalInfoView = z.infer<typeof portalInfoSchema>;

export const portalCodeRequestBodySchema = z.object({
  portalCode: portalCodeSchema,
  /** Any Kenyan mobile format; normalised on the server. Never logged. */
  phone: z.string().trim().min(9).max(20),
  language: uiLanguageSchema.default('en'),
});
export type PortalCodeRequestBody = z.infer<typeof portalCodeRequestBodySchema>;

/** The same answer whether or not the number has an account. */
export const portalCodeRequestResultSchema = z.object({
  challengeToken: portalChallengeTokenSchema,
  expiresInSeconds: z.number().int().positive(),
  resendAfterSeconds: z.number().int().min(0),
});
export type PortalCodeRequestResult = z.infer<typeof portalCodeRequestResultSchema>;

export const portalVerifyBodySchema = z.object({
  challengeToken: portalChallengeTokenSchema,
  code: portalOneTimeCodeSchema,
});

const personSchema = z.object({
  patientId: z.uuid(),
  givenName: z.string(),
  patientNumber: z.string(),
});

/** GET /portal/me. */
export const portalMeSchema = z.object({
  self: personSchema,
  /** Children the account may see (plan §4.3), checked again on every read. */
  dependants: z.array(personSchema),
  organisationName: z.string(),
  language: uiLanguageSchema,
  idleMinutes: z.number().int().positive(),
});
export type PortalMeView = z.infer<typeof portalMeSchema>;

export const portalLanguageBodySchema = z.object({ language: uiLanguageSchema });

export const portalAppointmentSchema = z.object({
  id: z.uuid(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  facilityName: z.string(),
  clinicName: z.string(),
  practitionerName: z.string().nullable(),
  kind: z.enum(['new', 'follow_up']),
});
export type PortalAppointmentView = z.infer<typeof portalAppointmentSchema>;

export const portalAppointmentsSchema = z.object({
  appointments: z.array(portalAppointmentSchema),
});

export const portalCancelAppointmentBodySchema = z.object({
  /** The patient's own words are optional; the record says it was cancelled in the portal. */
  reason: z.string().trim().max(200).optional(),
});

export const portalBillLineSchema = z.object({
  itemName: z.string(),
  quantity: z.number(),
  /** The patient's share after any approved waiver or credit. */
  patientAmount: amountSchema,
  /** What the payer (insurer, SHA) covers. */
  payerAmount: amountSchema,
  cancelled: z.boolean(),
});

export const portalReceiptSchema = z.object({
  number: z.string().nullable(),
  method: z.string(),
  amount: amountSchema,
  receivedAt: z.iso.datetime(),
  reference: z.string().nullable(),
});

export const portalBillSchema = z.object({
  visitId: z.uuid(),
  visitNumber: z.string(),
  facilityName: z.string(),
  startedAt: z.iso.datetime(),
  payerName: z.string(),
  lines: z.array(portalBillLineSchema),
  receipts: z.array(portalReceiptSchema),
  due: amountSchema,
  paid: amountSchema,
  owed: amountSchema,
  /** Lines without a price yet: the balance may still change. */
  unpricedCount: z.number().int().min(0),
  /** How to pay: the facility's paybill and the account number to quote (plan P16). */
  payment: z.object({ paybillNumber: z.string(), accountNumber: z.string() }).nullable(),
});
export type PortalBillView = z.infer<typeof portalBillSchema>;

export const portalBillsSchema = z.object({
  bills: z.array(portalBillSchema),
  /** The total still owed on the bills shown. */
  totalOwed: amountSchema,
});
export type PortalBillsView = z.infer<typeof portalBillsSchema>;

export const portalLabResultSchema = z.object({
  analyteName: z.string(),
  /** As a person reads it, e.g. "4.25", "< 0.50", "Positive", or "Not done". */
  display: z.string(),
  unit: z.string().nullable(),
  interpretation: z.enum(LAB_INTERPRETATIONS),
  /** e.g. "3.5 – 5.1"; null when no reference range is set. */
  range: z.string().nullable(),
  /** A written result is not shown here (plan E4): ask at the clinic. */
  withheld: z.boolean(),
});

export const portalLabReportSchema = z.object({
  id: z.uuid(),
  testName: z.string(),
  releasedAt: z.iso.datetime(),
  facilityName: z.string(),
  results: z.array(portalLabResultSchema),
  /**
   * A final culture: its outcome and organism names only; sensitivities and comments are
   * not shown (lab QC and microbiology plan Q12).
   */
  culture: z
    .object({
      outcome: z.enum(CULTURE_OUTCOMES),
      organisms: z.array(z.string()),
    })
    .nullable(),
});
export type PortalLabReportView = z.infer<typeof portalLabReportSchema>;

export const portalResultsSchema = z.object({ reports: z.array(portalLabReportSchema) });

export const portalAllergiesSchema = z.object({ allergies: allergySummarySchema });

// --- Staff: portal accounts --------------------------------------------------------------

export const PORTAL_ACCOUNT_STATUSES = ['active', 'suspended', 'closed'] as const;
export type PortalAccountStatus = (typeof PORTAL_ACCOUNT_STATUSES)[number];

/** GET /patients/:patientId/portal: what staff see about a patient's portal access. */
export const patientPortalSchema = z.object({
  account: z
    .object({
      id: z.uuid(),
      status: z.enum(PORTAL_ACCOUNT_STATUSES),
      statusReason: z.string().nullable(),
      /** The last four digits of the mobile the codes go to. */
      phoneEnding: z.string().nullable(),
      enrolledAt: z.iso.datetime(),
      lastSignedInAt: z.iso.datetime().nullable(),
      consentTextVersion: z.string(),
      dependants: z.array(
        z.object({
          linkId: z.uuid(),
          patientId: z.uuid(),
          name: z.string(),
          patientNumber: z.string(),
          /** False once the child turns 18, the relationship ends or a death is recorded. */
          visible: z.boolean(),
        }),
      ),
    })
    .nullable(),
  /** Whether the organisation's portal is switched on (Settings > Patient portal). */
  portalEnabled: z.boolean(),
  /** Children recorded as this patient's dependants who could be linked now (plan §4.3). */
  linkable: z.array(
    z.object({
      relationshipId: z.uuid(),
      patientId: z.uuid(),
      name: z.string(),
      patientNumber: z.string(),
    }),
  ),
  /** The patient's own active mobiles, for choosing where codes go at enrolment. */
  mobiles: z.array(z.object({ contactId: z.uuid(), phoneEnding: z.string() })),
});
export type PatientPortalView = z.infer<typeof patientPortalSchema>;

export const enrolPortalBodySchema = z.object({
  /** The patient's mobile in their contacts that the codes go to. */
  contactId: z.uuid(),
  /** The wording read to the patient (a consent text with purpose patient_portal). */
  consentTextId: z.uuid(),
  decision: z.literal('granted'),
  method: z.literal('verbal'),
});

/** Why: required for suspending, resuming, closing and ending a child's link. Never logged. */
export const portalStatusChangeBodySchema = z.object({
  reason: z.string().trim().min(5).max(500),
});

export const linkPortalDependantBodySchema = z.object({
  /** The active dependant relationship that makes the child the patient's dependant. */
  relationshipId: z.uuid(),
});

// --- Staff: settings ---------------------------------------------------------------------

export const portalSettingsSchema = z.object({
  enabled: z.boolean(),
  portalCode: z.string().nullable(),
  facilities: z.array(
    z.object({ id: z.uuid(), name: z.string(), paybillNumber: z.string().nullable() }),
  ),
});
export type PortalSettingsView = z.infer<typeof portalSettingsSchema>;

export const updatePortalSettingsBodySchema = z.object({
  enabled: z.boolean(),
  portalCode: portalCodeSchema,
});

export const PAYBILL_NUMBER_PATTERN = /^\d{5,7}$/;
export const setFacilityPaybillBodySchema = z.object({
  paybillNumber: z.string().trim().regex(PAYBILL_NUMBER_PATTERN).nullable(),
});

export const portalConsentTextSchema = z.object({
  id: z.uuid(),
  version: z.string(),
  language: z.enum(UI_LANGUAGES),
  body: z.string(),
  status: z.enum(['active', 'retired']),
  createdAt: z.iso.datetime(),
});
export type PortalConsentTextView = z.infer<typeof portalConsentTextSchema>;

export const portalConsentTextInputSchema = z.object({
  version: z.string().trim().min(1).max(40),
  language: z.enum(UI_LANGUAGES),
  body: z.string().trim().min(1).max(10_000),
});

// --- Staff: language ---------------------------------------------------------------------

/** GET and PUT /me/language: null means English, the default. */
export const userLanguageSchema = z.object({ language: uiLanguageSchema.nullable() });
export type UserLanguageView = z.infer<typeof userLanguageSchema>;
